'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Icon } from '@ironhack/design-system2/components/icon';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@ironhack/design-system2/components/modal';
import { Text } from '@ironhack/design-system2/components/text';
import { useDisclosure } from '@ironhack/design-system2/hooks';
import { X } from '@ironhack/design-system2/icons/x';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import { prop } from 'ramda';
import { type ReactElement, useEffect, useState } from 'react';

import { Button, getButtonTarget } from '@/app/components/client/button';
import { getHref } from '@/app/components/client/link';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { type BasePageParams } from '@/app/urls/url-mapping';
import { useExitIntent } from '@/hooks/use-exit-intent';
import { type DatoButtonBlock } from '@/lib/datocms/types';
import { pushToDataLayer } from '@/lib/gtm';
import { Form } from './form';
import { type LeadCapturePopupData } from './lead-capture-popup.queries';

const createOnClickForButton =
  (
    button: DatoButtonBlock,
    locale: Pick<BasePageParams, 'language' | 'region'>
  ) =>
  (): void => {
    const href = getHref(getButtonTarget(button), {
      ...locale,
      withOrigin: true,
    });
    pushToDataLayer({
      event: 'popup_click_cta',
      cta_url: href,
    });
  };

type Props = {
  data: LeadCapturePopupData;
} & BoxProps;

export const LeadCapturePopupClient = (props: Props): ReactElement | null => {
  const {
    data: {
      buttons,
      description,
      form,
      highlightedText,
      image,
      subtitle,
      title,
      triggers,
    },
    ...boxProps
  } = props;

  const flatTriggers = triggers.map(prop('triggerName'));
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    params: { language, region },
  } = usePageContext();

  useExitIntent(
    flatTriggers,
    () => {
      if (!isDismissed) onOpen();
    },
    [isDismissed, onOpen]
  );

  useEffect(() => {
    if (isOpen) pushToDataLayer({ event: 'popup_display' });
  }, [isOpen]);

  const closeAndTrack = (): void => {
    pushToDataLayer({ event: 'popup_close' });
    onClose();
  };

  const dismissPopup = (): void => {
    setIsDismissed(true);
  };

  return isDismissed ? null : (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={closeAndTrack}
      onCloseComplete={dismissPopup}
    >
      <ModalOverlay />
      <ModalContent
        // "safe" prevents clipping when the modal is taller than the screen
        alignSelf="safe center"
        background="background.white"
        borderRadius="2xl"
        // Overrides Chakra's narrower max-width
        maxWidth={[null, null, 'none']}
        mx={2}
        my={1}
        width={[null, null, '540px']}
        {...boxProps}
      >
        <ModalHeader as={Flex} justify="end" p={2}>
          <Icon
            _hover={{ background: 'background.light' }}
            as={X}
            borderRadius="sm"
            boxSize="24px"
            cursor="pointer"
            onClick={closeAndTrack}
          />
        </ModalHeader>

        <ModalBody
          as={Flex}
          direction="column"
          gap={2}
          pb={4}
          pt={0}
          px={[2, null, 4]}
        >
          {image && (
            <CmsImage
              data={image.responsiveImage}
              style={{ alignSelf: 'center' }}
            />
          )}

          {title && (
            <Text as="h1" textStyle="3xl">
              {title}
            </Text>
          )}

          {subtitle && (
            <Text as="h2" textStyle="xlBold">
              {subtitle}
            </Text>
          )}

          {highlightedText && (
            <Text
              alignSelf="start"
              background={`fill.${highlightedText.color.fillColor}`}
              color="text.inverse"
              px={1}
              py={0.5}
              textStyle="lBold"
            >
              {highlightedText.text}
            </Text>
          )}

          {!isEmptyDocument(description) && (
            <StructuredContent data={description} />
          )}

          {buttons.length > 0 && (
            <Flex
              direction={['column', null, 'row']}
              gap={2}
              width="full"
              wrap="wrap"
            >
              {buttons.map((button) => (
                <Button
                  flex="1"
                  key={button.text}
                  onClick={createOnClickForButton(button, { language, region })}
                  px={2}
                  {...button}
                />
              ))}
            </Flex>
          )}

          {form && <Form formContent={form} />}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
